/* eslint-disable camelcase */
import { BodyText, IconSet } from '@postscript/components';
import GroupedProfileEvent from 'components/conversations/components/GroupedProfileEvent';
import ProfileEvent from 'components/conversations/components/ProfileEvent';
import MessageComponent from 'components/sales/components/Message';
import { isSenderMessage } from 'components/sales/components/MessageMetadata';
import SystemMessage from 'components/sales/components/SystemMessage';
import {
  SYSTEM_EVENT_SALES_SUBSTRING,
  VIEW_OPTIONS,
} from 'components/sales/constants';
import {
  ChatHistory,
  Message,
  MessageGroup,
  Subscriber,
} from 'components/sales/types';
import { uniqueId } from 'lodash';
import moment from 'moment';
import styled from 'styled-components';
import utils from 'utils';

const TODAY = 'Today';
const YESTERDAY = 'Yesterday';
const MESSAGE_GROUP_TYPE = 'message-group';
export const PROFILE_EVENT_PREFIX = 'profile.';

type Groups = { [key: string]: Message[] };

interface MessageGroupTextProps {
  group: MessageGroup;
}

const isSystemMessage = (message: Message): boolean => {
  return message.type?.includes(SYSTEM_EVENT_SALES_SUBSTRING);
};

export const makeGroupNamePretty = (groupName: string): string => {
  if (groupName === TODAY) {
    return `${TODAY} - ${moment().format('ddd, MMM Do')}`;
  }

  if (groupName === YESTERDAY) {
    return `${YESTERDAY} - ${moment()
      .subtract(1, 'day')
      .format('ddd, MMM Do')}`;
  }

  const now = moment();
  const groupDate = moment(`${groupName} 00:00`, 'YYYY-MM-DD HH:mm');

  return groupDate.isBefore(now, 'year')
    ? groupDate.format('ddd, MMM Do, YYYY')
    : groupDate.format('ddd, MMM Do');
};

const ThreadTimestamp = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-1);
  padding: var(--spacing-2);

  &::before,
  &::after {
    background: var(--border-color-dim);
    display: block;
    content: '';
    height: 1px;
    flex: 1 0;
  }
`;

const MessageGroupText = ({ group }: MessageGroupTextProps): JSX.Element => (
  <ThreadTimestamp>
    <BodyText color="var(--text-color)" size="x-small">
      {makeGroupNamePretty(group.text)}
    </BodyText>
  </ThreadTimestamp>
);

const generateKey = (
  subscriber: Subscriber,
  chatHistory: ChatHistory,
): string => {
  if (chatHistory.incoming_messages_id) {
    return `${subscriber.subscriber.id}_${chatHistory.incoming_messages_id}`;
  }
  if (chatHistory.sent_messages_id) {
    return `${subscriber.subscriber.id}_${chatHistory.sent_messages_id}`;
  }
  return uniqueId(`${subscriber.subscriber.id}_`);
};

export const createMessageForSubscriber = (
  subscriber: Subscriber,
  chatHistory: ChatHistory,
): Message => {
  const createdAt = moment.utc(chatHistory.created_at).local();
  const isProfileEvent = chatHistory.type?.includes(PROFILE_EVENT_PREFIX);
  return {
    type: chatHistory.type,
    type_id: chatHistory.type_id,
    createdAt,
    sent_message_id: chatHistory?.sent_messages_id || 0,
    text: utils.safeBase64Decode(chatHistory.body),
    media_url: chatHistory.media_url || chatHistory.data?.MediaUrl0,
    preview_link_map: chatHistory.preview_link_map,
    direction: isProfileEvent ? 'incoming' : chatHistory.direction,
    transactional: isProfileEvent ? false : chatHistory.transactional,
    user_guid: isProfileEvent ? 'profile_event' : chatHistory.user_guid,
    key: isProfileEvent
      ? `profile_${subscriber.subscriber.id}_${createdAt.valueOf()}`
      : generateKey(subscriber, chatHistory),
    user_first_name: chatHistory.user_first_name,
    user_last_name: chatHistory.user_last_name,
    user_avatar_url: chatHistory.user_avatar_url,
    eventData: chatHistory.data,
    is_sent_by_salesbub: chatHistory.is_sent_by_salesbub,
    subtype: chatHistory?.subtype,
    scheduled: chatHistory.scheduled,
    scheduledAt: chatHistory.scheduled_at,
    feedback: chatHistory.feedback,
    triggered_keyword_id: chatHistory.triggered_keyword_id,
    triggered_keyword: chatHistory.triggered_keyword,
    rcs_message: chatHistory.rcs_message,
  };
};

export const getGroupNameForMessage = ({ createdAt }: Message): string => {
  const now = moment();
  const dayOf = now.clone().startOf('day');
  const dayBefore = dayOf.clone().subtract(1, 'days');

  if (createdAt.isSameOrAfter(dayOf)) {
    return TODAY;
  }

  if (createdAt.isSameOrAfter(dayBefore) && createdAt.isBefore(dayOf)) {
    return YESTERDAY;
  }

  return createdAt.format('YYYY-MM-DD');
};

export const createGroups = (messages: Message[]): Groups => {
  const groups: Groups = {};
  messages.forEach((message: Message) => {
    const group = getGroupNameForMessage(message);
    if (groups[group]) {
      groups[group].push(message);
    } else {
      groups[group] = [message];
    }
  });

  return groups;
};

export const sortGroupNames = (names: string[]): string[] => {
  return names
    .filter((name) => ![TODAY, YESTERDAY].includes(name))
    .sort()
    .concat([YESTERDAY, TODAY]);
};

export const getMessagesWithGroups = (
  groups: Groups,
  view: string,
): (Message | MessageGroup)[] => {
  const groupNamesSorted = sortGroupNames(Object.keys(groups));

  return groupNamesSorted.reduce((messages: any[], name: string) => {
    const groupMessages = groups[name];
    if (!groupMessages?.length) return messages;

    const groupHasOnlySystemMessages =
      groupMessages.filter((m: Message) => !isSystemMessage(m)).length === 0;
    if (view === VIEW_OPTIONS.MERCHANT && groupHasOnlySystemMessages)
      return messages;

    groupMessages.sort((a: Message, b: Message) =>
      a.createdAt.isAfter(b.createdAt) ? 1 : -1,
    );

    return [
      ...messages,
      { type: MESSAGE_GROUP_TYPE, text: name },
      ...groupMessages,
    ];
  }, []);
};

export const getSubscriberChatItems = (
  subscriber: Subscriber,
  view: string,
): (Message | MessageGroup)[] => {
  const groups: Groups = createGroups(
    subscriber.chat_history.map((c) =>
      createMessageForSubscriber(subscriber, c),
    ),
  );
  return getMessagesWithGroups(groups, view);
};

export interface MessageListItem {
  key: string;
  el: JSX.Element;
}

const groupSequentialProfileEvents = (
  items: (Message | MessageGroup)[],
): (Message | MessageGroup)[] => {
  const result: (Message | MessageGroup)[] = [];

  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];

    // Skip non-profile events
    if (
      item.type === MESSAGE_GROUP_TYPE ||
      !(item as Message).type?.startsWith(PROFILE_EVENT_PREFIX)
    ) {
      result.push(item);
    } else {
      const currentMessage = item as Message;
      const currentType = currentMessage.type;
      const group = [currentMessage];

      // Look for consecutive events of same type
      for (let j = i + 1; j < items.length; j += 1) {
        const nextItem = items[j];

        // Stop if we hit non-profile event or different type
        if (
          nextItem.type === MESSAGE_GROUP_TYPE ||
          !(nextItem as Message).type?.startsWith(PROFILE_EVENT_PREFIX) ||
          (nextItem as Message).type !== currentType
        ) {
          break;
        }

        group.push(nextItem as Message);
        i = j; // Skip these items in outer loop
      }

      // Add grouped or individual message
      if (group.length > 1) {
        result.push({
          ...currentMessage,
          key: `${currentMessage.key}-grouped-${group.length}`,
          groupedMessages: group,
        } as Message);
      } else {
        result.push(currentMessage);
      }
    }
  }

  return result;
};

export const getMessageListItemsForSubscriber = (
  subscriber: Subscriber,
  view: string,
  feedback_items?: any,
): MessageListItem[] => {
  if (!subscriber) return [];

  if (feedback_items) {
    subscriber.chat_history.forEach((chatItem: any) => {
      if (chatItem.sent_messages_id && chatItem.direction === 'outgoing') {
        const feedbackItem = feedback_items?.data?.[chatItem.sent_messages_id];
        if (feedbackItem?.feedback_type) {
          // eslint-disable-next-line no-param-reassign
          chatItem.feedback = feedbackItem.feedback_type;
        }
      }
    });
  }

  const items = getSubscriberChatItems(subscriber, view);

  // Group sequential profile events of the same type
  const groupedItems = groupSequentialProfileEvents(items);

  const lastSenderMessageIndex = groupedItems.reduceRight(
    (lastIndex: number, item: Message | MessageGroup, currentIndex: number) =>
      lastIndex === -1 &&
      item.type !== MESSAGE_GROUP_TYPE &&
      isSenderMessage(item as Message)
        ? currentIndex
        : lastIndex,
    -1,
  );

  return groupedItems.map((item, i) => {
    if (item.type === MESSAGE_GROUP_TYPE) {
      return {
        key: item.text,
        el: <MessageGroupText group={item} />,
      };
    }

    const message = item as Message;

    if (message.type?.startsWith(PROFILE_EVENT_PREFIX)) {
      // Check if this is a grouped message
      const { groupedMessages } = message as any;
      if (groupedMessages && groupedMessages.length > 1) {
        return {
          key: message.key,
          el: <GroupedProfileEvent messages={groupedMessages} />,
        };
      }
      return {
        key: message.key,
        el: <ProfileEvent message={{ ...message, direction: 'profile' }} />,
      };
    }

    if (message.type?.includes(SYSTEM_EVENT_SALES_SUBSTRING))
      return { key: message.key, el: <SystemMessage message={message} /> };

    return {
      key: message.key,
      el: (
        <MessageComponent
          message={message}
          shopName={subscriber?.shop_name}
          id={i.toString()}
          subscriber={subscriber}
          isLastSenderMessage={i === lastSenderMessageIndex}
        />
      ),
    };
  });
};

export const getSystemMessageIcon = (text: string) => {
  let icon = IconSet.Bub;
  const lowerText = text.toLowerCase();

  // Subscriber opt in/out events
  if (lowerText.includes('opted in') || lowerText.includes('opts in')) {
    icon = IconSet.UserPlus;
  }
  if (
    lowerText.includes('opted out') ||
    lowerText.includes('opts out') ||
    lowerText.includes('subscriber triggered opt out')
  ) {
    icon = IconSet.Forbid;
  }

  // Site interaction events
  if (lowerText.includes('visited site')) {
    icon = IconSet.Navigation;
  }
  if (
    lowerText.includes('clicked link') ||
    lowerText.includes('the subscriber clicked this link')
  ) {
    icon = IconSet.CursorClick;
  }

  // Product interaction events
  if (lowerText.includes('browsed') || lowerText.includes('product browsed')) {
    icon = IconSet.Search;
  }
  if (
    lowerText.includes('added to cart') ||
    lowerText.includes('added to their cart')
  ) {
    icon = IconSet.ShoppingCart;
  }
  if (lowerText.includes('started checkout')) {
    icon = IconSet.ShoppingCart;
  }
  if (lowerText.includes('purchased') || lowerText.includes('order created')) {
    icon = IconSet.Dollar;
  }

  // Flow and conversation events
  if (lowerText.includes('flow branch split')) {
    icon = IconSet.Split;
  }
  if (lowerText.includes('resolved conversation for subscriber')) {
    icon = IconSet.CheckCircle;
  }
  if (lowerText.includes('was assigned a conversation')) {
    icon = IconSet.UserPlus;
  }
  if (lowerText.includes('flow message from flow')) {
    icon = IconSet.Message;
  }
  if (lowerText.includes('routed to a flow')) {
    icon = IconSet.Navigation;
  }

  // Search events
  if (lowerText.includes('subscriber searched for')) {
    icon = IconSet.Search;
  }

  // Shopify specific
  if (lowerText.includes('shopify')) {
    icon = IconSet.Dollar;
  }

  return icon;
};
